import React from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import Image from '../AppImage';
import { formatRequestAmount, splitRequestItems } from './requestBuilderUtils';
import { getPrimaryImage } from '../../utils/imageUtils';
import FinalPriceEditor from '../pricing/FinalPriceEditor';
import { formatItemPrice, isRangePriceFinalized, isRangePriced } from '../../utils/pricingUtils';

const ItemRow = ({ item, editable, onRemove, onQuantityChange, compact, showImages, detailsLayout, dense, standardThumbnail, onFinalizePrice }) => {
  const calculatedLineTotal = (Number(item.price) || 0) * (Number(item.quantity) || 1);
  const lineTotal = item.totalPrice != null && Number.isFinite(Number(item.totalPrice))
    ? Number(item.totalPrice)
    : calculatedLineTotal;
  const isService = item.type === 'service';
  const itemTypeLabel = isService ? 'Service' : item.type === 'product' ? 'Product' : 'Item';
  const atStockLimit = item.currentStock != null && item.quantity >= item.currentStock;
  const isRange = isRangePriced(item);
  const isFinalized = isRangePriceFinalized(item);

  return (
    <div className={`flex items-start justify-between gap-3 ${compact || dense ? 'py-2' : 'py-3'}`}>
      {showImages && (
        <div className={`${dense && !standardThumbnail ? 'h-11 w-11' : 'h-14 w-14'} flex-shrink-0 overflow-hidden rounded-lg bg-muted ${standardThumbnail ? '' : 'border border-border'}`}>
          {item.imageUrl ? (
            <Image
              src={getPrimaryImage(item.imageUrl)}
              alt={item.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <Icon name="ImageOff" size={standardThumbnail ? 24 : dense ? 16 : 20} />
            </div>
          )}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className={`${detailsLayout ? 'font-caption font-semibold' : 'font-medium'} ${dense ? 'text-sm' : ''} truncate text-foreground`}>{item.name}</p>
          {!detailsLayout && item.type !== 'item' && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium capitalize text-muted-foreground">
              {item.type}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {detailsLayout
            ? <>{itemTypeLabel} &middot; Qty {Number(item.quantity) || 1}</>
            : <>{formatItemPrice(item)} {isService ? '· fixed quantity' : 'each'}</>}
        </p>

        {detailsLayout && isRange && (
          <p className={`mt-1 text-xs font-caption ${isFinalized ? 'text-success' : 'text-warning'}`}>
            {isFinalized ? 'Final price confirmed' : `Price range: ${formatItemPrice(item)}`}
          </p>
        )}

        {detailsLayout && isRange && !isFinalized && onFinalizePrice && (
          <FinalPriceEditor item={item} onFinalize={(value) => onFinalizePrice(item, value)} />
        )}

        {editable && (
          <div className="mt-2 flex items-center gap-2">
            {isService ? (
              <span className="rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-muted-foreground">
                Quantity 1
              </span>
            ) : (
              <div className="inline-flex items-center rounded-md border border-border bg-background">
                <button
                  type="button"
                  onClick={() => onQuantityChange(item.id, item.quantity - 1)}
                  disabled={item.quantity <= 1}
                  className="p-1.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                  aria-label={`Decrease ${item.name} quantity`}
                >
                  <Icon name="Minus" size={14} />
                </button>
                <span className="min-w-8 text-center text-sm font-semibold text-foreground">
                  {item.quantity}
                </span>
                <button
                  type="button"
                  onClick={() => onQuantityChange(item.id, item.quantity + 1)}
                  disabled={atStockLimit}
                  className="p-1.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                  aria-label={`Increase ${item.name} quantity`}
                >
                  <Icon name="Plus" size={14} />
                </button>
              </div>
            )}
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => onRemove(item.id)}
              className="text-muted-foreground hover:text-destructive"
            >
              Remove
            </Button>
          </div>
        )}
      </div>

      <div className="text-right">
        <p className={`${dense ? 'text-sm' : detailsLayout ? 'text-base' : ''} ${detailsLayout ? 'font-caption' : ''} font-semibold text-foreground`}>
          {isRange && !isFinalized ? formatItemPrice(item) : formatRequestAmount(lineTotal)}
        </p>
        {!detailsLayout && !editable && item.quantity > 1 && (
          <p className="mt-1 text-xs text-muted-foreground">Qty {item.quantity}</p>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, items, ...rowProps }) => (
  <section>
    {title && (
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
    )}
    <div className="divide-y divide-border">
      {items.map((item) => <ItemRow key={item.id} item={item} {...rowProps} />)}
    </div>
  </section>
);

const RequestItemGroups = ({
  items,
  editable = false,
  onRemove,
  onQuantityChange,
  compact = false,
  showImages = false,
  detailsLayout = false,
  dense = false,
  standardThumbnail = false,
  onFinalizePrice,
}) => {
  const { services, products, unclassified } = splitRequestItems(items);
  const hasCombinedRequest = services.length > 0 && products.length > 0;
  const rowProps = { editable, onRemove, onQuantityChange, compact, showImages, detailsLayout, dense, standardThumbnail, onFinalizePrice };

  if (hasCombinedRequest) {
    return (
      <div className={`rounded-xl bg-primary/5 p-3 md:p-4 ${detailsLayout ? '' : 'border border-primary/30'}`}>
        <div className="mb-2 flex items-center gap-2 text-primary">
          <Icon name="Link2" size={16} />
          <span className="text-xs font-semibold uppercase tracking-wide">Requested together</span>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          These services and products will be reviewed as one combined request.
        </p>
        <div className="space-y-3 border-l-2 border-primary/30 pl-3">
          <Section title="Services" items={services} {...rowProps} />
          <Section title="Products" items={products} {...rowProps} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {services.length > 0 && <Section title="Services" items={services} {...rowProps} />}
      {products.length > 0 && <Section title="Products" items={products} {...rowProps} />}
      {unclassified.length > 0 && <Section title="Items" items={unclassified} {...rowProps} />}
    </div>
  );
};

export default RequestItemGroups;
